import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { Charge, ChargeCode } from 'src/app/core/models/charge.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';

@Component({
	templateUrl: './add-charge.component.html',
	styleUrls: ['./add-charge.component.scss']
})
export class AddChargeComponent implements OnInit {

	@Input()
	appointmentId: number;

	isLoaded = false;
	pageTitle = 'Add Additional Charge';
	form: FormGroup;
	additionalCharges: ChargeCode[];
	billableOptions = ['Yes', 'No'];

	constructor(
		private modalController: ModalController,
		private storage: LocalStorageService,
		private formBuilder: FormBuilder,
		private alertController: AlertController,
		private toastController: ToastController,
		private appointmentService: AppointmentService,
		private dateHelper: DateHelperService,
	) { }

	get chargeCodeControl() {
		return this?.form.controls.chargeCode;
	}

	get billableControl() {
		return this?.form.controls.billable;
	}

	get quantityControl() {
		return this?.form.controls.qty;
	}

	async ngOnInit(): Promise<void> {
		this.form = this.formBuilder.group(
			{
				chargeCode: [(null as ChargeCode), Validators.required],
				qty: [1, Validators.compose([Validators.required, Validators.min(1), Validators.max(999)])],
				billable: [false, Validators.required],
			},
		);
		this.additionalCharges = await this.getCharges();
		this.isLoaded = true;
	}

	async getCharges(): Promise<ChargeCode[]> {
		const charges = await this.storage.additionalCharges.getAll();
		return this.sortCharges(charges);
	}

	sortCharges(charges: ChargeCode[]): ChargeCode[] {
		charges.sort((a, b) => a.desc > b.desc ? 1 : -1);
		return charges;
	}

	async dismiss(): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true,
		});
	}

	async onClickAddCharge(): Promise<void> {
		this.markAsTouched(this.form);

		if (this.form.invalid) {
			await this.alertInvalidForm();
			return;
		}

		await this.toastChargeAddSuccess();
		await this.save(this.form.value);
		this.dismiss();
	}

	async save(charge: Charge): Promise<void> {
		charge.timestamp = this.dateHelper.getNowISO();

		const state = await this.appointmentService.getClearCodeState(this.appointmentId);

		state.clearCode.charges.push(charge);

		this.storage.appointmentClearCode.setItem(this.appointmentId, state);
	}

	markAsTouched(form: FormGroup): void {
		for (const key in form.controls) {
			if (!form.controls.hasOwnProperty(key)) {
				continue;
			}

			form.controls[key].setValue(form.controls[key].value);
			form.controls[key].markAsTouched();
		}
	}

	async alertInvalidForm(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Add Charge Failed',
			message: 'Required fields need reviewed',
			buttons: ['OK'],
		});

		await alert.present();
	}

	async toastChargeAddSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Charge Added Successfully',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	compareWith(o1, o2): boolean {
		return o1 && o2 ? o1.id === o2.id : o1 === o2;
	}

}
